<template>
    <drawer-modal body-class="attendance-timeline-drawer__body" @close-modal="$emit('close')">
        <template slot="header">
            <div class="attendance-timeline-drawer__header-shell">
                <div>
                    <p class="attendance-timeline-drawer__kicker mb-1">{{ $t('attendance_timeline') }}</p>
                    <h5 class="mb-1">{{ employeeName }}</h5>
                    <p class="text-muted mb-0">{{ formattedDate }}</p>
                </div>
                <button type="button" class="close outline-none" @click.prevent="$emit('close')">
                    <span>
                        <app-icon name="x"></app-icon>
                    </span>
                </button>
            </div>
        </template>

        <template slot="body">
            <div class="position-relative">
                <app-overlay-loader v-if="isLoading" />

                <div v-if="!isLoading && timeline" class="attendance-timeline-drawer__content" :class="{ 'loading-opacity': isLoading }">
                    <section class="attendance-timeline-drawer__summary card border-0 mb-4">
                        <div class="card-body">
                            <div class="attendance-timeline-drawer__summary-head">
                                <div class="attendance-timeline-drawer__identity">
                                    <div class="attendance-timeline-drawer__avatar">
                                        <img
                                            :src="timeline.employee?.photo_url || getAppUrl('images/avatar-demo.jpg')"
                                            :alt="employeeName"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                    <div>
                                        <h6 class="mb-1">{{ employeeName }}</h6>
                                        <p class="text-muted mb-0">{{ timeline.employee?.matricule || '-' }}</p>
                                    </div>
                                </div>

                                <div class="attendance-timeline-drawer__badges">
                                    <span class="attendance-status-badge" :class="statusClass(timeline.status)">
                                        {{ statusLabel(timeline.status) }}
                                    </span>
                                    <span
                                        v-if="timeline.day_context?.is_holiday"
                                        class="attendance-status-badge attendance-status-badge--holiday"
                                    >
                                        {{ $t('holiday') }}
                                    </span>
                                    <span
                                        v-else-if="timeline.day_context?.is_day_off"
                                        class="attendance-status-badge attendance-status-badge--day-off"
                                    >
                                        {{ $t('day_off') }}
                                    </span>
                                </div>
                            </div>

                            <div v-if="timeline.day_context?.holiday" class="attendance-timeline-drawer__holiday-note mt-3">
                                <app-icon name="calendar" class="mr-2" />
                                <div>
                                    <strong>{{ timeline.day_context.holiday.name }}</strong>
                                    <p class="mb-0 text-muted">{{ timeline.day_context.holiday.description || $t('holiday') }}</p>
                                </div>
                            </div>

                            <div class="attendance-timeline-drawer__metric-grid mt-4">
                                <div class="attendance-metric-card">
                                    <span>{{ $t('clock_in') }}</span>
                                    <strong>{{ formatDateTimeValue(timeline.clock_in) }}</strong>
                                </div>
                                <div class="attendance-metric-card">
                                    <span>{{ $t('clock_out') }}</span>
                                    <strong>{{ formatDateTimeValue(timeline.clock_out) }}</strong>
                                </div>
                                <div class="attendance-metric-card">
                                    <span>{{ $t('duration') }}</span>
                                    <strong>{{ formatDuration(timeline.total_hours) }}</strong>
                                </div>
                                <div class="attendance-metric-card">
                                    <span>{{ $t('shift') }}</span>
                                    <strong>{{ timeline.shift?.name || '-' }}</strong>
                                </div>
                                <div class="attendance-metric-card">
                                    <span>{{ $t('location') }}</span>
                                    <strong>{{ timeline.location?.name || '-' }}</strong>
                                </div>
                                <div class="attendance-metric-card">
                                    <span>{{ $t('device') }}</span>
                                    <strong>{{ primaryDevice }}</strong>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="card border-0 mb-4 attendance-timeline-drawer__timeline-card">
                        <div class="card-body">
                            <div class="attendance-section-head mb-3">
                                <div>
                                    <h6 class="mb-1">{{ $t('activity_timeline') }}</h6>
                                    <p class="text-muted mb-0">{{ $t('attendance_timeline_drawer_hint') }}</p>
                                </div>
                            </div>

                            <div class="attendance-timeline-drawer__timeline-list">
                                <div
                                    v-for="item in timelineSteps"
                                    :key="item.key"
                                    class="attendance-timeline-step"
                                    :class="{ 'is-missing': !item.value }"
                                >
                                    <div class="attendance-timeline-step__icon">
                                        <app-icon :name="item.icon" />
                                    </div>
                                    <div class="attendance-timeline-step__content">
                                        <div class="d-flex justify-content-between align-items-start flex-wrap">
                                            <div>
                                                <strong>{{ item.label }}</strong>
                                                <p class="mb-0 text-muted">{{ item.help }}</p>
                                            </div>
                                            <span class="attendance-timeline-step__value">{{ item.value || '-' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="card border-0 mb-4" v-if="timeline.anomalies && timeline.anomalies.length">
                        <div class="card-body">
                            <div class="attendance-section-head mb-3">
                                <div>
                                    <h6 class="mb-1">{{ $t('attendance_anomalies') }}</h6>
                                    <p class="text-muted mb-0">{{ $t('attendance_anomalies_hint') }}</p>
                                </div>
                            </div>
                            <div class="attendance-anomaly-list">
                                <div v-for="anomaly in timeline.anomalies" :key="anomaly.code" class="attendance-anomaly-item">
                                    <app-icon name="alert-triangle" class="mr-2" />
                                    <div>
                                        <strong>{{ anomalyLabel(anomaly.code) }}</strong>
                                        <p class="mb-0 text-muted">{{ anomaly.message }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="card border-0 mb-4">
                        <div class="card-body">
                            <div class="attendance-section-head mb-3">
                                <div>
                                    <h6 class="mb-1">{{ $t('google_maps') }}</h6>
                                    <p class="text-muted mb-0">{{ $t('attendance_map_hint') }}</p>
                                </div>
                            </div>

                            <div v-if="mapUnavailableMessage" class="attendance-map-state attendance-map-state--empty">
                                <app-icon name="map-pin" />
                                <p class="mb-0">{{ mapUnavailableMessage }}</p>
                            </div>

                            <div v-else>
                                <div ref="map" class="attendance-timeline-drawer__map"></div>
                                <div class="attendance-map-legend mt-3">
                                    <span class="attendance-map-legend__item">
                                        <span class="attendance-map-legend__swatch attendance-map-legend__swatch--clock"></span>
                                        {{ $t('clock_in') }}
                                    </span>
                                    <span v-if="timeline.location" class="attendance-map-legend__item">
                                        <span class="attendance-map-legend__swatch attendance-map-legend__swatch--worksite"></span>
                                        {{ $t('location') }}
                                    </span>
                                    <span v-if="hasGeofence" class="attendance-map-legend__item">
                                        <span class="attendance-map-legend__swatch attendance-map-legend__swatch--geofence"></span>
                                        {{ $t('geofence') }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="card border-0 mb-4" v-if="timeline.events && timeline.events.length">
                        <div class="card-body">
                            <div class="attendance-section-head mb-3">
                                <div>
                                    <h6 class="mb-1">{{ $t('attendance_event_log') }}</h6>
                                    <p class="text-muted mb-0">{{ $t('attendance_event_log_hint') }}</p>
                                </div>
                            </div>

                            <div class="attendance-event-list">
                                <div v-for="event in timeline.events" :key="event.id" class="attendance-event-item">
                                    <div class="attendance-event-item__head">
                                        <span class="attendance-status-badge" :class="event.type === 'in' ? 'attendance-status-badge--ontime' : 'attendance-status-badge--neutral'">
                                            {{ event.type === 'in' ? $t('clock_in') : $t('clock_out') }}
                                        </span>
                                        <strong>{{ formatDateTimeValue(event.time) }}</strong>
                                    </div>
                                    <div class="attendance-event-item__meta">
                                        <span>{{ event.location?.name || '-' }}</span>
                                        <span>{{ event.device?.identifier || '-' }}</span>
                                        <span :class="event.gps?.within_geofence ? 'text-success' : 'text-warning'">
                                            {{ event.gps?.within_geofence ? $t('is_within_geofence') : $t('outside_geofence_short') }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </template>
    </drawer-modal>
</template>

<script>
import DrawerModal from '../../Helpers/Modal/DrawerModal.vue';
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { ATTENDANCE_TIMELINE } from '../../../Config/ApiUrl';

const FALLBACK_GOOGLE_MAPS_KEY = 'AIzaSyA8emh2Pq3wIootXyoX5zILgIqaxxOrs-E';

export default {
    name: 'AttendanceTimelineDrawer',
    extends: CoreLibrary,
    components: { DrawerModal },
    props: {
        record: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            timeline: null,
            map: null,
            mapMarker: null,
            worksiteMarker: null,
            geofenceOverlay: null,
            mapError: false,
        };
    },
    computed: {
        employeeName() {
            return this.timeline?.employee?.full_name || this.record?.employee?.full_name || '-';
        },
        formattedDate() {
            return this.formatDateValue(this.timeline?.date || this.record?.date);
        },
        primaryDevice() {
            return this.timeline?.device_info?.devices?.[0] || this.$t('not_available');
        },
        mapFocusPoint() {
            const clockInGps = (this.timeline?.gps_points || []).find((point) => point.type === 'in');
            return clockInGps || this.timeline?.gps_points?.[0] || null;
        },
        hasGeofence() {
            const location = this.timeline?.location;
            if (!location) {
                return false;
            }

            return Boolean(
                (location.geofence_type === 'polygon' && location.geojson)
                || ((location.geofence_type === 'radius' || location.radius || location.radius_m) && location.latitude && location.longitude)
            );
        },
        mapUnavailableMessage() {
            if (this.mapError) {
                return this.$t('google_maps_failed_to_load');
            }

            if (!this.mapFocusPoint && !this.timeline?.location?.latitude) {
                return this.$t('attendance_map_unavailable');
            }

            return '';
        },
        timelineSteps() {
            return [
                {
                    key: 'clock_in',
                    label: this.$t('clock_in'),
                    value: this.formatDateTimeValue(this.timeline?.clock_in),
                    help: this.$t('attendance_clock_in_help'),
                    icon: 'log-in',
                },
                {
                    key: 'break_start',
                    label: this.$t('break_start'),
                    value: this.formatDateTimeValue(this.timeline?.break_start),
                    help: this.$t('attendance_break_start_help'),
                    icon: 'coffee',
                },
                {
                    key: 'break_end',
                    label: this.$t('break_end'),
                    value: this.formatDateTimeValue(this.timeline?.break_end),
                    help: this.$t('attendance_break_end_help'),
                    icon: 'play-circle',
                },
                {
                    key: 'clock_out',
                    label: this.$t('clock_out'),
                    value: this.formatDateTimeValue(this.timeline?.clock_out),
                    help: this.$t('attendance_clock_out_help'),
                    icon: 'log-out',
                },
            ];
        },
        googleMapsApiKey() {
            return process.env.MIX_GOOGLE_MAPS_API_KEY || FALLBACK_GOOGLE_MAPS_KEY;
        },
    },
    mounted() {
        this.loadTimeline();
    },
    beforeDestroy() {
        this.clearMapOverlays();
    },
    methods: {
        async loadTimeline() {
            this.isLoading = true;
            this.mapError = false;
            this.timeline = null;

            try {
                const url = this.record?.timeline_url
                    ? this.record.timeline_url.replace(this.getBaseUrl(), '').replace(/^\//, '')
                    : `${ATTENDANCE_TIMELINE}/${this.record.employee_id}/timeline?date=${this.record.date}`;
                const response = await this.axiosGet(url);
                this.timeline = response.data;
                this.$nextTick(async () => {
                    try {
                        await this.renderMap();
                    } catch (error) {
                        this.mapError = true;
                    }
                });
            } catch (error) {
                this.$toastr.e(this.$t('error_loading_attendance_timeline'));
            } finally {
                this.isLoading = false;
            }
        },
        formatDateValue(value) {
            if (!value) {
                return '-';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return new Intl.DateTimeFormat(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            }).format(date);
        },
        formatDateTimeValue(value) {
            if (!value) {
                return null;
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return new Intl.DateTimeFormat(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            }).format(date);
        },
        formatDuration(value) {
            const totalHours = Number(value || 0);

            if (!Number.isFinite(totalHours) || totalHours <= 0) {
                return '-';
            }

            const totalMinutes = Math.round(totalHours * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            if (!minutes) {
                return `${hours}h`;
            }

            return `${hours}h ${minutes}m`;
        },
        statusLabel(status) {
            return this.$t(status || 'untracked');
        },
        statusClass(status) {
            const classes = {
                on_time: 'attendance-status-badge--ontime',
                present: 'attendance-status-badge--present',
                late: 'attendance-status-badge--late',
                absent: 'attendance-status-badge--absent',
                overtime: 'attendance-status-badge--overtime',
                suspicious: 'attendance-status-badge--suspicious',
                untracked: 'attendance-status-badge--neutral',
            };

            return classes[status] || classes.untracked;
        },
        anomalyLabel(code) {
            const labels = {
                missing_clock_out: this.$t('missing_clock_out'),
                incomplete_break: this.$t('incomplete_break'),
                outside_geofence: this.$t('outside_geofence'),
                location_mismatch: this.$t('location_mismatch'),
                attendance_on_day_off: this.$t('attendance_on_day_off'),
                multiple_devices: this.$t('multiple_devices'),
            };

            return labels[code] || code;
        },
        async loadGoogleMaps() {
            if (window.google && window.google.maps) {
                return window.google.maps;
            }

            if (window.__mozaliGoogleMapsPromise) {
                return window.__mozaliGoogleMapsPromise;
            }

            window.__mozaliGoogleMapsPromise = new Promise((resolve, reject) => {
                const existingScript = document.getElementById('mozali-google-maps');

                if (existingScript) {
                    existingScript.addEventListener('load', () => resolve(window.google.maps));
                    existingScript.addEventListener('error', reject);
                    return;
                }

                const script = document.createElement('script');
                script.id = 'mozali-google-maps';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=geometry`;
                script.async = true;
                script.defer = true;
                script.onload = () => resolve(window.google.maps);
                script.onerror = reject;
                document.head.appendChild(script);
            });

            return window.__mozaliGoogleMapsPromise;
        },
        async renderMap() {
            if (this.mapUnavailableMessage || !this.$refs.map) {
                return;
            }

            await this.loadGoogleMaps();
            this.clearMapOverlays();

            const fallbackCenter = {
                lat: Number(this.timeline?.location?.latitude || this.mapFocusPoint?.latitude || -11.6645),
                lng: Number(this.timeline?.location?.longitude || this.mapFocusPoint?.longitude || 27.4793),
            };

            this.map = new window.google.maps.Map(this.$refs.map, {
                center: fallbackCenter,
                zoom: 15,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            });

            const bounds = new window.google.maps.LatLngBounds();

            if (this.timeline?.location?.latitude && this.timeline?.location?.longitude) {
                const worksitePosition = {
                    lat: Number(this.timeline.location.latitude),
                    lng: Number(this.timeline.location.longitude),
                };

                this.worksiteMarker = new window.google.maps.Marker({
                    map: this.map,
                    position: worksitePosition,
                    title: this.timeline.location.name || this.$t('location'),
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 9,
                        fillColor: '#2563eb',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                    },
                });
                bounds.extend(worksitePosition);
            }

            if (this.hasGeofence) {
                this.renderGeofence(bounds);
            }

            if (this.mapFocusPoint?.latitude && this.mapFocusPoint?.longitude) {
                const clockInPosition = {
                    lat: Number(this.mapFocusPoint.latitude),
                    lng: Number(this.mapFocusPoint.longitude),
                };

                this.mapMarker = new window.google.maps.Marker({
                    map: this.map,
                    position: clockInPosition,
                    title: this.$t('clock_in'),
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: '#10b981',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                    },
                });
                bounds.extend(clockInPosition);
            }

            if (!bounds.isEmpty()) {
                this.map.fitBounds(bounds, 64);
            }
        },
        renderGeofence(bounds) {
            const location = this.timeline?.location;
            if (!location) {
                return;
            }

            if (location.geofence_type === 'polygon' && location.geojson) {
                const path = this.geoJsonToPath(location.geojson);
                if (!path.length) {
                    return;
                }

                this.geofenceOverlay = new window.google.maps.Polygon({
                    map: this.map,
                    paths: path,
                    strokeColor: '#7c3aed',
                    strokeOpacity: 0.9,
                    strokeWeight: 2,
                    fillColor: '#7c3aed',
                    fillOpacity: 0.12,
                });

                path.forEach((point) => bounds.extend(point));
                return;
            }

            if (location.latitude && location.longitude && (location.radius_m || location.radius)) {
                this.geofenceOverlay = new window.google.maps.Circle({
                    map: this.map,
                    center: {
                        lat: Number(location.latitude),
                        lng: Number(location.longitude),
                    },
                    radius: Number(location.radius_m || location.radius),
                    strokeColor: '#7c3aed',
                    strokeOpacity: 0.85,
                    strokeWeight: 2,
                    fillColor: '#7c3aed',
                    fillOpacity: 0.1,
                });

                const circleBounds = this.geofenceOverlay.getBounds();
                if (circleBounds) {
                    bounds.union(circleBounds);
                }
            }
        },
        geoJsonToPath(geojson) {
            if (!geojson || !window.google?.maps) {
                return [];
            }

            const geometry = geojson.type === 'Feature' ? geojson.geometry : geojson;
            if (!geometry || geometry.type !== 'Polygon') {
                return [];
            }

            const ring = geometry.coordinates?.[0] || [];
            return ring
                .filter((point) => Array.isArray(point) && point.length >= 2)
                .map((point) => ({ lat: Number(point[1]), lng: Number(point[0]) }))
                .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
        },
        clearMapOverlays() {
            if (this.mapMarker) {
                this.mapMarker.setMap(null);
                this.mapMarker = null;
            }

            if (this.worksiteMarker) {
                this.worksiteMarker.setMap(null);
                this.worksiteMarker = null;
            }

            if (this.geofenceOverlay) {
                this.geofenceOverlay.setMap(null);
                this.geofenceOverlay = null;
            }
        },
    },
};
</script>

<style scoped>
.attendance-timeline-drawer__body {
    padding: 1.25rem;
}

.attendance-timeline-drawer__header-shell {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.attendance-timeline-drawer__kicker {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0f766e;
}

.attendance-timeline-drawer__summary,
.attendance-timeline-drawer__timeline-card,
.attendance-timeline-drawer__content .card {
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
    border-radius: 1rem;
}

.attendance-timeline-drawer__summary-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.attendance-timeline-drawer__identity {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.attendance-timeline-drawer__avatar {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 999px;
    overflow: hidden;
    background: #e2e8f0;
    flex-shrink: 0;
}

.attendance-timeline-drawer__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.attendance-timeline-drawer__badges {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.attendance-status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    padding: 0.45rem 0.8rem;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1;
}

.attendance-status-badge--ontime,
.attendance-status-badge--present {
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
}

.attendance-status-badge--late {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.attendance-status-badge--absent {
    background: rgba(239, 68, 68, 0.14);
    color: #b91c1c;
}

.attendance-status-badge--overtime {
    background: rgba(59, 130, 246, 0.14);
    color: #1d4ed8;
}

.attendance-status-badge--suspicious {
    background: rgba(168, 85, 247, 0.15);
    color: #7e22ce;
}

.attendance-status-badge--holiday {
    background: rgba(14, 165, 233, 0.14);
    color: #0369a1;
}

.attendance-status-badge--day-off,
.attendance-status-badge--neutral {
    background: rgba(100, 116, 139, 0.14);
    color: #475569;
}

.attendance-timeline-drawer__holiday-note {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.9rem 1rem;
    border-radius: 0.9rem;
    background: rgba(14, 165, 233, 0.08);
    color: #0f172a;
}

.attendance-timeline-drawer__metric-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
}

.attendance-metric-card {
    padding: 0.95rem 1rem;
    border-radius: 0.95rem;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.18);
}

.attendance-metric-card span {
    display: block;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 0.35rem;
}

.attendance-metric-card strong {
    color: #0f172a;
    font-size: 0.95rem;
}

.attendance-section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.attendance-timeline-drawer__timeline-list,
.attendance-anomaly-list,
.attendance-event-list {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.attendance-timeline-step,
.attendance-anomaly-item,
.attendance-event-item {
    display: flex;
    gap: 0.85rem;
    padding: 0.95rem 1rem;
    border-radius: 1rem;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.16);
}

.attendance-timeline-step.is-missing {
    opacity: 0.65;
}

.attendance-timeline-step__icon {
    width: 2.25rem;
    height: 2.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgba(37, 99, 235, 0.12);
    color: #2563eb;
    flex-shrink: 0;
}

.attendance-timeline-step__content,
.attendance-anomaly-item > div,
.attendance-event-item {
    flex: 1 1 auto;
}

.attendance-timeline-step__value {
    font-weight: 700;
    color: #0f172a;
}

.attendance-timeline-drawer__map {
    min-height: 300px;
    border-radius: 1rem;
    overflow: hidden;
    background: #e2e8f0;
}

.attendance-map-state {
    min-height: 220px;
    border-radius: 1rem;
    background: #f8fafc;
    border: 1px dashed rgba(148, 163, 184, 0.45);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: #64748b;
    text-align: center;
    padding: 1.5rem;
}

.attendance-map-legend {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.attendance-map-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    color: #475569;
    font-size: 0.84rem;
    font-weight: 600;
}

.attendance-map-legend__swatch {
    display: inline-block;
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 999px;
}

.attendance-map-legend__swatch--clock {
    background: #10b981;
}

.attendance-map-legend__swatch--worksite {
    background: #2563eb;
}

.attendance-map-legend__swatch--geofence {
    background: #7c3aed;
}

.attendance-event-item {
    flex-direction: column;
}

.attendance-event-item__head,
.attendance-event-item__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.attendance-event-item__meta {
    font-size: 0.84rem;
    color: #64748b;
}

@media (max-width: 767.98px) {
    .attendance-timeline-drawer__metric-grid {
        grid-template-columns: 1fr;
    }
}
</style>
